import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router';
import { routes } from '@config/routes';
import { AppShell } from '@shared/components/layout/AppShell';
import { AuthGuard } from '@core/auth/AuthGuard';
import { ApprovalGuard } from '@features/registration/ApprovalGuard';
import { SubscriptionGuard } from '@features/billing/SubscriptionGuard';
import { SubscribePage } from '@features/billing/SubscribePage';
import { AuthCallbackPage } from '@core/auth/AuthCallbackPage';
import { BrandLoader } from '@shared/ui';
import { featureManifest } from './featureManifest';
import { featureBacktesting, featurePlans } from '@config/env';

// Lazy: keeps amazon-cognito-identity-js (SRP crypto) out of the main bundle — authenticated
// sessions never load it. All five pre-auth screens share one layout + the cognito lib, so Vite
// dedupes them into a shared chunk.
const LoginPage = lazy(() => import('@features/auth-pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@features/auth-pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const VerifyEmailPage = lazy(() => import('@features/auth-pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('@features/auth-pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const NewPasswordPage = lazy(() => import('@features/auth-pages/NewPasswordPage').then(m => ({ default: m.NewPasswordPage })));
// Public legal pages (no auth, no shell) — reachable from the consent banner (pre-auth) + Settings.
const TermsPage = lazy(() => import('@features/privacy/pages/TermsPage').then(m => ({ default: m.TermsPage })));
// AI Backtester results (authenticated, inside the shell) — lazy like every feature page.
const StrategyResultsPage = lazy(() => import('@features/backtesting/pages/StrategyResultsPage').then(m => ({ default: m.StrategyResultsPage })));
// Checkout return page (`Stripe:SuccessUrl`) — reached rarely (once per checkout), so it stays lazy
// like every other feature page; `SubscribePage` is NOT lazy here because `SubscriptionGuard`
// (imported eagerly, right above) already pulls it into the main bundle.
const BillingSuccessPage = lazy(() => import('@features/billing/BillingSuccessPage').then(m => ({ default: m.BillingSuccessPage })));
// Credit-pack checkout return page (`Stripe:CreditPack:SuccessUrl`) — same rationale as above.
const CreditsSuccessPage = lazy(() => import('@features/billing/CreditsSuccessPage').then(m => ({ default: m.CreditsSuccessPage })));

function preAuth(element: ReactNode) {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen w-full items-center justify-center bg-canvas">
          <BrandLoader />
        </div>
      }
    >
      {element}
    </Suspense>
  );
}

// Route-level fallback for every lazy page. Was two grey `Skeleton` bars until 2026-08-29;
// now the animated brand mark, centred in whatever container the route renders into —
// `flex-1` fills AppShell's `min-h-full` content column, `min-h-[60vh]` gives the two Stripe
// return routes (siblings of the shell, no flex parent) something to centre inside.
function PageLoading() {
  return (
    <div className="flex min-h-[60vh] w-full flex-1 flex-col items-center justify-center gap-3">
      <BrandLoader decorative />
      <p className="text-sm text-text-muted">Loading…</p>
    </div>
  );
}

export const routeTree: RouteObject[] = [
  // Pre-auth screens (mounted OUTSIDE AuthGuard, like the callback route).
  { path: routes.login,          element: preAuth(<LoginPage />) },
  { path: routes.register,       element: preAuth(<RegisterPage />) },
  { path: routes.verifyEmail,    element: preAuth(<VerifyEmailPage />) },
  { path: routes.forgotPassword, element: preAuth(<ForgotPasswordPage />) },
  { path: routes.newPassword,    element: preAuth(<NewPasswordPage />) },
  // NO /privacy route since 2026-08-21 (in-app policy retired — links go to the Optimus Futures
  // main-site policy, PRIVACY_POLICY_URL) and NO /cookies route since 2026-08-24 (cookie policy
  // removed pending a replacement; its links render DISABLED — user: "remind me about it on a
  // later stage"). Both paths fall to the catch-all.
  { path: routes.terms,          element: preAuth(<TermsPage />) },
  {
    path: routes.authCallback,
    element: <AuthCallbackPage />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <ApprovalGuard>
          <Outlet />
        </ApprovalGuard>
      </AuthGuard>
    ),
    children: [
      // Pathless layout route: everything the paywall gates. `SubscriptionGuard` renders `SubscribePage`
      // in place of `AppShell` (and everything nested under it) for an unentitled user.
      {
        element: (
          <SubscriptionGuard>
            <AppShell />
          </SubscriptionGuard>
        ),
        children: [
          { index: true, element: <Navigate to={routes.dashboard} replace /> },
          // AI Backtester per-strategy results page (spec §1D) — child of the shell, sibling of the
          // manifest routes (the manifest only carries top-level nav pages).
          //
          // Because it is NOT in the manifest, `featureManifest`'s own filter does not reach it: with
          // the feature flagged off, dropping the nav item and /backtesting would still have left every
          // saved strategy openable by URL. Spread-in conditionally so the route simply does not exist
          // when the feature is off, and the `*` catch-all below sends those links to the dashboard.
          ...(featureBacktesting
            ? [{
                path: 'backtesting/:strategyId',
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <StrategyResultsPage />
                  </Suspense>
                ),
                id: 'backtesting-results',
              }]
            : []),
          // Reachable for an ALREADY-entitled user (ProfileMenu's "Upgrade plan"/"Plan" row, the nav
          // lock, ProUpsell) — `SubscriptionGuard` separately renders this same `SubscribePage` in
          // place of the shell (as `standalone`) for an UNENTITLED one. Here, nested under AppShell's
          // `<Outlet/>`, it stays framed (default `standalone={false}`) per the approved Figma: the
          // sidebar + topbar remain visible, matching every other page in the manifest.
          // Gated with the rest of billing (2026-08-17). With plans off there is nothing to buy, so the
          // pricing page must not exist even by URL — every in-app link to it is hidden in the same
          // change, and the `*` catch-all sends a stale bookmark to the dashboard.
          ...(featurePlans
            ? [{ path: routes.upgrade.slice(1), element: <SubscribePage />, id: 'upgrade' }]
            : []),
          ...featureManifest.map(({ id, path, Page }) => ({
            path: path.slice(1),
            element: (
              <Suspense fallback={<PageLoading />}>
                <Page />
              </Suspense>
            ),
            // RouteObject doesn't have an id slot we use, but keeping it as the key keeps the
            // intent local — react-router accepts the `id` field for route identification.
            id,
          })),
        ],
      },
      // `/billing/success` (Stripe:SuccessUrl) is a SIBLING of the SubscriptionGuard/AppShell layout
      // above (C1 fix, 2026-08-13) — it used to be nested INSIDE it, so it NEVER MOUNTED for the exact
      // caller who needs it most: an unentitled user returning from a successful Stripe Checkout
      // landed here, but `SubscriptionGuard` (still reading the pre-webhook, unentitled `/me`)
      // rendered `SubscribePage` in place of `AppShell` instead of ever reaching this route's element,
      // showing the paywall again instead of "Finalizing your subscription…". `BillingSuccessPage` is
      // a `fixed inset-0` full-screen takeover with its own `useMe()` poll + the live
      // `subscription.changed` unlock (see `useSubscriptionChangedInvalidation`) — it needs no shell
      // and no paywall gate, only `AuthGuard`/`ApprovalGuard` (a signed-in, approved user is the one
      // precondition that's still relevant here).
      // Both Stripe RETURN pages are gated with plans (2026-08-17): they only ever follow a checkout,
      // and no checkout can start while the feature is off.
      ...(featurePlans
        ? [{
            path: routes.billingSuccess.slice(1),
            element: (
              <Suspense fallback={<PageLoading />}>
                <BillingSuccessPage />
              </Suspense>
            ),
            id: 'billing-success',
          }]
        : []),
      // `/billing/credits/success` (Stripe:CreditPack:SuccessUrl) — a SIBLING of the
      // SubscriptionGuard/AppShell layout for the same reason as `/billing/success` above, and one
      // that bites even harder here: the whole point of the credit pack is that a FREE-tier user who
      // has run out can buy their way back in, and that user is precisely the one `SubscriptionGuard`
      // would replace with the paywall. Nested, they'd pay and then land on "Choose your plan".
      ...(featurePlans
        ? [{
            path: routes.creditsSuccess.slice(1),
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreditsSuccessPage />
              </Suspense>
            ),
            id: 'credits-success',
          }]
        : []),
    ],
  },
  // Catch-all (I3): any unmatched path — a stale bookmark, a typo, a dead deep link — bounces to the
  // dashboard redirect instead of react-router's blank "no route matched" render. MUST be last: route
  // order is match priority here, and a `*` earlier would shadow every path declared after it.
  { path: '*', element: <Navigate to={routes.root} replace /> },
];
